import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchProducts } from "../store/slices/productSlice";
import { fetchMainCategories } from "../store/slices/mainCategorySlice";
import { fetchCategoriesByMainCategory } from "../store/slices/categorySlice";
import FilterSidebar from "../components/listing/FilterSidebar";
import ProductCard, {
  ProductCardSkeleton,
} from "../components/home/ProductCard";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { RootState, AppDispatch } from "../store";
import { useLocation, Link } from "react-router-dom";
import { MdFilterList, MdSort } from "react-icons/md";
import { AiOutlineClose } from "react-icons/ai";
import { FaAngleRight, FaArrowLeft } from "react-icons/fa";

const ProductListing: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { products, loading, error } = useSelector(
    (state: RootState) => state.product,
  );
  const { mainCategories } = useSelector(
    (state: RootState) => state.mainCategory,
  );
  const { categories, hoveredCategories } = useSelector(
    (state: RootState) => state.category,
  );

  const location = useLocation();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [hasInitializedCategory, setHasInitializedCategory] = useState(false);
  const [hasInitializedBrand, setHasInitializedBrand] = useState(false);

  useEffect(() => {
    if (mainCategories.length === 0) {
      dispatch(fetchMainCategories());
    }
  }, [dispatch, mainCategories.length]);

  // Fetch categories belonging to the main category from URL
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const main_category = queryParams.get("main_category");
    if (main_category) {
      dispatch(fetchCategoriesByMainCategory(main_category));
    }
    setSelectedCategories([]); // Reset selection when category changes
  }, [dispatch, location.search]);

  // Dynamic product query based on URL and selected category tags
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const filters: any = {};

    const main_category = queryParams.get("main_category");
    const category = queryParams.get("category");
    const subcategory = queryParams.get("subcategory");

    if (main_category) filters.main_category = main_category;

    if (selectedCategories.length > 0) {
      filters.category_id = { $in: selectedCategories };
    } else if (category) {
      filters.category_id = category;
    }

    if (subcategory) filters.subcategory_id = subcategory;

    dispatch(fetchProducts({ filter: filters, limit: 1000 }));
  }, [dispatch, location.search, selectedCategories]);

  useEffect(() => {
    if (isFilterOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isFilterOpen]);

  const queryParams = new URLSearchParams(location.search);
  const mainCategoryIdFromUrl = queryParams.get("main_category");
  const matchedMainCategory = mainCategories.find(
    (mc) => mc._id === mainCategoryIdFromUrl,
  );

  // Filter categories that match this active main category
  const rawMatching = mainCategoryIdFromUrl
    ? hoveredCategories[mainCategoryIdFromUrl] ||
      categories.filter((c: any) => {
        const categoryMainId =
          c.main_category?._id || c.main_category || c.main_category_id;
        return categoryMainId === mainCategoryIdFromUrl;
      })
    : [];

  const matchingCategories = [...rawMatching].sort(
    (a: any, b: any) =>
      (Number(a.priority) || 999) - (Number(b.priority) || 999),
  );

  // Extract unique brands from the loaded products of current category selection
  const availableBrands = React.useMemo(() => {
    const brandMap = new Map<string, any>();
    products.forEach((product) => {
      const brand = (product.brand_id || product.brand) as any;
      if (brand && brand._id) {
        brandMap.set(brand._id, brand);
      }
    });
    return Array.from(brandMap.values());
  }, [products]);

  // Filter products by selected brands locally
  const displayedProducts = React.useMemo(() => {
    if (selectedBrands.length === 0) return products;
    return products.filter((product) => {
      const brand = (product.brand_id || product.brand) as any;
      return brand && selectedBrands.includes(brand._id);
    });
  }, [products, selectedBrands]);

  // Reset category initialization when URL category changes
  useEffect(() => {
    setHasInitializedCategory(false);
  }, [mainCategoryIdFromUrl]);

  // Default select first category once matchingCategories is loaded
  useEffect(() => {
    if (matchingCategories.length > 0 && !hasInitializedCategory) {
      setSelectedCategories([matchingCategories[0]._id!]);
      setHasInitializedCategory(true);
    }
  }, [matchingCategories, hasInitializedCategory]);

  // Reset brand initialization when selected categories or main category changes
  useEffect(() => {
    setHasInitializedBrand(false);
    setSelectedBrands([]);
  }, [selectedCategories, mainCategoryIdFromUrl]);

  // Default select first brand once availableBrands is populated
  useEffect(() => {
    if (availableBrands.length > 0 && !hasInitializedBrand) {
      setSelectedBrands([availableBrands[0]._id!]);
      setHasInitializedBrand(true);
    }
  }, [availableBrands, hasInitializedBrand]);

  let bannerTitle = "";
  let bannerImage = "";
  let bannerTag = "";
  let bannerSubtitle = "";

  if (matchedMainCategory) {
    bannerTitle = matchedMainCategory.name;
    bannerImage =
      matchedMainCategory.image || "/images/categories/placeholder.png";
    bannerTag = "";
    bannerSubtitle = matchedMainCategory.description || "";
  }

  const sidebarWidth = "240px";

  return (
    <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
      <style>
        {`
                .no-scrollbar::-webkit-scrollbar { display: none; }
                .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

                /* Breadcrumbs */
                .breadcrumb {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 11px;
                    font-weight: 900;
                    text-transform: uppercase;
                    letter-spacing: 2px;
                    color: #94a3b8;
                    margin-bottom: 24px;
                    flex-wrap: wrap;
                    text-align: left;
                }

                .breadcrumb a {
                    color: #94a3b8;
                    text-decoration: none;
                    transition: color 0.3s ease;
                }

                .breadcrumb a:hover {
                    color: #43c8ca;
                }

                .breadcrumb .active {
                    color: #43c8ca;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 200px;
                }

                .product-listing-wrapper {
                    max-width: 1440px;
                    margin: 15px auto;
                    padding: 140px 20px 40px 20px;
                    box-sizing: border-box;
                }

                .category-page-banner {
                    width: 100%;
                    height: 240px;
                    border-radius: 16px;
                    overflow: hidden;
                    position: relative;
                    margin-bottom: 30px;
                    box-shadow: 0 12px 30px rgba(0,0,0,0.08);
                    display: flex;
                    align-items: center;
                    background-size: cover;
                    background-position: center;
                    background-repeat: no-repeat;
                    transition: all 0.3s ease;
                }

                .category-page-banner::before {
                    content: '';
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(90deg, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.45) 60%, rgba(0, 0, 0, 0.15) 100%);
                    z-index: 1;
                }

                .banner-content {
                    position: relative;
                    z-index: 2;
                    padding: 0 45px;
                    max-width: 700px;
                    color: #fff;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    text-align: left;
                }

                .banner-tag {
                    font-size: 11px;
                    font-weight: 700;
                    color: var(--color-primary);
                    text-transform: uppercase;
                    letter-spacing: 2px;
                }

                .banner-title {
                    font-size: 34px;
                    font-weight: 800;
                    margin: 0;
                    letter-spacing: -0.5px;
                    text-shadow: 0 2px 4px rgba(0,0,0,0.2);
                    color: #ffffff;
                }

                .banner-subtitle {
                    font-size: 14px;
                    color: #e2e8f0;
                    line-height: 1.5;
                    margin: 0;
                }

                @media (max-width: 768px) {
                    .category-page-banner {
                        height: 200px;
                        margin-bottom: 20px;
                        border-radius: 12px;
                    }
                    .banner-content {
                        padding: 0 24px;
                    }
                    .banner-title {
                        font-size: 26px;
                    }
                    .banner-subtitle {
                        font-size: 13px;
                    }
                }

                .listing-main-body {
                    display: flex;
                    align-items: flex-start;
                    position: relative;
                }

                .tags-row {
                    display: flex;
                    gap: 10px;
                    margin-bottom: 30px;
                    flex-wrap: wrap;
                    padding-left: 20px;
                }

                .selection-tag {
                    background-color: #fff;
                    border: 1px solid #d4d5d9;
                    border-radius: 0px;
                    padding: 5px 15px;
                    font-size: 11px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    cursor: pointer;
                    color: #3e4152;
                    transition: all 0.2s ease;
                }

                .selection-tag:hover {
                    border-color: #dbeb83;
                    color: #5a6a00;
                    background-color: #fafde9;
                }

                .selection-tag.active-tag {
                    background-color: var(--color-primary, #43c8ca);
                    border-color: var(--color-primary, #43c8ca);
                    color: #fff !important;
                    font-weight: 600;
                    box-shadow: 0 4px 10px rgba(67, 200, 202, 0.2);
                }

                /* ── Category Box Design ── */
                .category-boxes-container {
                    padding: 0;
                    margin-bottom: 25px;
                }
                
                .category-boxes-grid {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 12px;
                }

                .category-box-card {
                    background-color: #ffffff;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 12px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                    width: 105px;
                    height: 105px;
                    position: relative;
                    box-sizing: border-box;
                }

                .category-box-card:hover {
                    border-color: #43c8ca;
                    box-shadow: 0 4px 12px rgba(67, 200, 202, 0.1);
                    transform: translateY(-2px);
                }

                .category-box-card.active-box {
                    border-color: #43c8ca;
                    background-color: #f0fdfa;
                    box-shadow: 0 4px 15px rgba(67, 200, 202, 0.15);
                }

                .category-box-image-container {
                    width: 44px;
                    height: 44px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 8px;
                    background-color: #f1f5f9;
                    overflow: hidden;
                    margin-bottom: 8px;
                    transition: all 0.2s;
                }

                .category-box-card.active-box .category-box-image-container {
                    background-color: #ccfbf1;
                }

                .category-box-image {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .category-box-avatar {
                    font-size: 16px;
                    font-weight: 700;
                    color: #475569;
                }

                .category-box-card.active-box .category-box-avatar {
                    color: #0d9488;
                }

                .category-box-name {
                    font-size: 11px;
                    font-weight: 500;
                    color: #334155;
                    text-align: center;
                    width: 100%;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .category-box-card.active-box .category-box-name {
                    color: #0f766e;
                    font-weight: 600;
                }

                .category-box-check {
                    position: absolute;
                    top: 6px;
                    right: 6px;
                    width: 16px;
                    height: 16px;
                    border-radius: 50%;
                    background-color: #43c8ca;
                    color: #ffffff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 9px;
                    font-weight: bold;
                }

                .product-listing-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 30px 15px;
                }

                @media (max-width: 1200px) {
                    .product-listing-grid { grid-template-columns: repeat(3, 1fr); }
                }

                .content-grid-wrapper {
                    padding-left: 20px;
                }

                @media (max-width: 900px) {
                    .product-listing-wrapper { padding: 160px 16px 60px 16px !important; }
                    .desktop-sidebar { display: none !important; }
                    .product-listing-grid { grid-template-columns: repeat(2, 1fr); gap: 15px 10px; }
                    .control-bar { display: none !important; }
                    .mobile-filter-bar { display: none !important; }
                    .breadcrumbs-row { display: none !important; }
                    .tags-row { padding-left: 0 !important; margin-bottom: 15px !important; gap: 8px !important; }
                    .floating-elements { display: none !important; }
                    .content-grid-wrapper { padding-left: 0 !important; }
                    .listing-main-body { flex-direction: column !important; }
                    .listing-footer-wrapper { display: none !important; }
                }

                @media (max-width: 600px) {
                    .product-listing-wrapper { padding: 160px 12px 60px 12px !important; }
                }

                @media (max-width: 480px) {
                    .product-listing-wrapper { padding: 160px 8px 60px 8px !important; }
                    .product-listing-grid { grid-template-columns: repeat(2, 1fr); gap: 12px 8px; }
                }

                @keyframes slideIn {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }

                .filter-drawer {
                    position: fixed;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    background: white;
                    height: 80vh;
                    z-index: 1000;
                    border-top-left-radius: 24px;
                    border-top-right-radius: 24px;
                    box-shadow: 0 -10px 40px rgba(0,0,0,0.15);
                    animation: slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    display: flex;
                    flex-direction: column;
                    padding-top: 8px;
                }

                .filter-drawer::before {
                    content: '';
                    display: block;
                    width: 36px;
                    height: 4px;
                    background-color: #e2e8f0;
                    border-radius: 2px;
                    margin: 8px auto 4px auto;
                    flex-shrink: 0;
                }

                .drawer-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(15, 23, 42, 0.55);
                    backdrop-filter: blur(4px);
                    z-index: 999;
                    animation: fadeInOverlay 0.25s ease-out;
                }

                @keyframes fadeInOverlay {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                `}
      </style>
      <Header />

      <div className="product-listing-wrapper">
        {/* Header Actions: Breadcrumbs & Back to Home */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <div className="breadcrumb" style={{ marginBottom: 0 }}>
            <Link to="/">Home</Link>
            <FaAngleRight size={10} />
            <span className="active">
              {matchedMainCategory?.name || "Products"}
            </span>
          </div>
          <Link to="/" style={{ 
             display: 'flex', 
             alignItems: 'center', 
             gap: '8px',
             padding: '10px 20px', 
             backgroundColor: '#01bdc0', 
             color: '#ffffff', 
             borderRadius: '8px', 
             fontSize: '14px', 
             fontWeight: 600,
             textDecoration: 'none',
             boxShadow: '0 4px 12px rgba(1, 189, 192, 0.3)',
             transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => { 
            e.currentTarget.style.backgroundColor = '#01a0a3'; 
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(1, 189, 192, 0.4)';
          }}
          onMouseOut={(e) => { 
            e.currentTarget.style.backgroundColor = '#01bdc0'; 
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(1, 189, 192, 0.3)';
          }}
          >
            <FaArrowLeft size={14} /> Back to Home
          </Link>
        </div>

        {/* Category Premium Banner */}
        {matchedMainCategory && (
          <div
            className="category-page-banner"
            style={{ backgroundImage: `url(${bannerImage})` }}
          >
            <div className="banner-content">
              <span className="banner-tag">{bannerTag}</span>
              <h1 className="banner-title">{bannerTitle}</h1>
              <p className="banner-subtitle">{bannerSubtitle}</p>
            </div>
          </div>
        )}

        {/* Main Body: Sidebar + Content */}
        <div className="listing-main-body">
          {/* Filter Column (Desktop) */}
          {/* <div className="desktop-sidebar no-scrollbar" style={{
                        width: sidebarWidth,
                        flexShrink: 0,
                        position: 'sticky',
                        top: '210px',
                        height: 'calc(100vh - 220px)',
                        overflowY: 'auto',
                        paddingRight: '10px'
                    }}>
                        <FilterSidebar hideHeader={true} />
                    </div> */}

          {/* Product Presentation Column */}
          <div style={{ flex: 1 }} className="product-content-area">
            {/* Active Selections Tags */}
            {matchingCategories.length > 0 && (
              <div className="category-boxes-container">
                <div style={{ fontWeight: 600, fontSize: "14px", color: "#1e293b", marginBottom: "12px" }}>
                  Filter by Category:
                </div>
                <div className="category-boxes-grid">
                  {matchingCategories.map((c) => {
                    const isSelected = selectedCategories.includes(c._id!);
                    return (
                      <div
                        key={c._id}
                        className={`category-box-card ${isSelected ? "active-box" : ""}`}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedCategories([]);
                          } else {
                            setSelectedCategories([c._id!]);
                          }
                        }}
                      >
                        <div className="category-box-image-container">
                          {c.image ? (
                            <img src={encodeURI(c.image)} alt={c.name} className="category-box-image" />
                          ) : (
                            <div className="category-box-avatar">
                              {c.name ? c.name.charAt(0).toUpperCase() : "C"}
                            </div>
                          )}
                        </div>
                        <div className="category-box-name" title={c.name}>{c.name}</div>
                        {isSelected && (
                          <div className="category-box-check">
                            ✓
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Filter by Brand */}
            {availableBrands.length > 0 && (
              <div className="category-boxes-container" style={{ marginTop: "24px" }}>
                <div style={{ fontWeight: 600, fontSize: "14px", color: "#1e293b", marginBottom: "12px" }}>
                  Filter by Brand:
                </div>
                <div className="category-boxes-grid">
                  {availableBrands.map((b) => {
                    const isSelected = selectedBrands.includes(b._id!);
                    const brandImage = b.logo || b.image;
                    return (
                      <div
                        key={b._id}
                        className={`category-box-card ${isSelected ? "active-box" : ""}`}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedBrands([]);
                          } else {
                            setSelectedBrands([b._id!]);
                          }
                        }}
                      >
                        <div className="category-box-image-container">
                          {brandImage ? (
                            <img src={brandImage} alt={b.name} className="category-box-image" />
                          ) : (
                            <div className="category-box-avatar">
                              {b.name ? b.name.charAt(0).toUpperCase() : "B"}
                            </div>
                          )}
                        </div>
                        <div className="category-box-name" title={b.name}>{b.name}</div>
                        {isSelected && (
                          <div className="category-box-check">
                            ✓
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="content-grid-wrapper">
              {loading ? (
                <div className="product-listing-grid">
                  {Array(8)
                    .fill(0)
                    .map((_, index) => (
                      <ProductCardSkeleton key={index} />
                    ))}
                </div>
              ) : error ? (
                <div
                  style={{
                    textAlign: "center",
                    color: "#e53e3e",
                    padding: "50px",
                  }}
                >
                  Error: {error}
                </div>
              ) : displayedProducts.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "50px",
                    fontSize: "18px",
                    color: "#666",
                  }}
                >
                  No products found.
                </div>
              ) : (
                <div className="product-listing-grid">
                  {displayedProducts.map((product) => {
                    const discount = Math.round(
                      ((product.mrp - product.price) / product.mrp) * 100,
                    );
                    const firstImage =
                      product.images && product.images.length > 0
                        ? typeof product.images[0] === "string"
                          ? product.images[0]
                          : (product.images[0] as any).url
                        : "https://via.placeholder.com/300x400?text=No+Image";

                    return (
                      <ProductCard
                        key={product._id}
                        _id={product._id}
                        image={firstImage}
                        name={product.name}
                        price={product.price}
                        originalPrice={product.mrp}
                        off={discount > 0 ? `${discount}%` : undefined}
                        star={4.5}
                        ratingCount={"100"}
                        tag={
                          (product.brand_id as any)?.name ||
                          (product.brand as any)?.name ||
                          "HoneyCool"
                        }
                        images={product.images as any}
                        is_featured={product.is_featured}
                        is_new={product.is_new}
                        isHotDeal={product.stock > 0 && product.stock < 10}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Filter & Sort Bar */}
      <div
        className="mobile-filter-bar"
        style={{
          display: "none",
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          height: "54px",
          backgroundColor: "white",
          borderTop: "1px solid #edeff2",
          zIndex: 900,
          boxShadow: "0 -2px 10px rgba(0,0,0,0.05)",
        }}
      >
        <button
          onClick={() => setIsFilterOpen(true)}
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            border: "none",
            background: "none",
            fontSize: "14px",
            fontWeight: 700,
            color: "#282c3f",
            borderRight: "1px solid #edeff2",
          }}
        >
          <MdSort style={{ fontSize: "18px" }} /> SORT
        </button>
        <button
          onClick={() => setIsFilterOpen(true)}
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            border: "none",
            background: "none",
            fontSize: "14px",
            fontWeight: 700,
            color: "#282c3f",
          }}
        >
          <MdFilterList style={{ fontSize: "18px" }} /> FILTER
        </button>
      </div>

      {/* Mobile Filter Drawer */}
      {isFilterOpen && (
        <>
          <div
            className="drawer-overlay"
            onClick={() => setIsFilterOpen(false)}
          />
          <div className="filter-drawer">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px 20px",
                borderBottom: "1px solid #edeff2",
              }}
            >
              <span
                style={{
                  fontWeight: 800,
                  fontSize: "16px",
                  textTransform: "uppercase",
                }}
              >
                Filters
              </span>
              <AiOutlineClose
                style={{ fontSize: "20px", cursor: "pointer" }}
                onClick={() => setIsFilterOpen(false)}
              />
            </div>
            <div
              style={{ flex: 1, overflowY: "auto" }}
              className="no-scrollbar"
            >
              <FilterSidebar hideHeader={true} />
            </div>
            <div
              style={{
                padding: "12px 20px",
                borderTop: "1px solid #edeff2",
                display: "flex",
                gap: "15px",
              }}
            >
              <button
                onClick={() => setIsFilterOpen(false)}
                style={{
                  flex: 1,
                  height: "44px",
                  background: "#fff",
                  border: "1px solid #d4d5d9",
                  borderRadius: "4px",
                  fontWeight: 700,
                }}
              >
                CLOSE
              </button>
              <button
                onClick={() => setIsFilterOpen(false)}
                style={{
                  flex: 1,
                  height: "44px",
                  background: "var(--color-primary)",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  fontWeight: 700,
                }}
              >
                APPLY
              </button>
            </div>
          </div>
        </>
      )}

      <div className="listing-footer-wrapper">
        <Footer />
      </div>
    </div>
  );
};

export default ProductListing;
